import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { apiGet, API_AVAILABLE } from "../lib/api";
import Seo from "../components/Seo";

function renderContent(md) {
  const lines = md.split("\n");
  const out = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="my-6 space-y-3 list-none">
          {listBuffer.map((li, i) => (
            <li key={i} className="flex gap-3 text-[16px] leading-relaxed text-[#333] font-light">
              <span className="mt-3 w-3 h-px bg-[#FFA500] shrink-0"></span>
              <span dangerouslySetInnerHTML={{ __html: li.replace(/\*\*(.+?)\*\*/g, "<strong class='text-[#1A1A1A] font-medium'>$1</strong>") }} />
            </li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };
  lines.forEach((line, idx) => {
    if (line.startsWith("## ")) {
      flushList();
      out.push(<h2 key={`h-${idx}`} className="mt-12 mb-4 font-display text-3xl md:text-4xl leading-tight text-[#1A1A1A] tracking-tight">{line.slice(3)}</h2>);
    } else if (line.startsWith("- ")) {
      listBuffer.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      flushList();
      out.push(
        <p key={`ol-${idx}`} className="my-3 text-[16px] leading-relaxed text-[#333] font-light pl-7 relative">
          <span className="absolute left-0 top-0 ticker-num text-[#FFA500]">{line.match(/^\d+/)[0]}</span>
          <span dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\.\s/, "").replace(/\*\*(.+?)\*\*/g, "<strong class='text-[#1A1A1A] font-medium'>$1</strong>") }} />
        </p>
      );
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      out.push(
        <p key={`p-${idx}`} className="my-5 text-[16px] md:text-[17px] leading-relaxed text-[#333] font-light">
          <span dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, "<strong class='text-[#1A1A1A] font-medium'>$1</strong>") }} />
        </p>
      );
    }
  });
  flushList();
  return out;
}

export default function BlogPost() {
  useReveal();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setPost(null);
    setNotFound(false);
    if (!API_AVAILABLE) {
      setNotFound(true);
      return;
    }
    apiGet(`/blog/posts/${slug}`).then((data) => {
      if (data) setPost(data);
      else setNotFound(true);
    });
    apiGet("/blog/posts", { expectArray: true }).then((data) => {
      if (data) setRelated(data.filter((p) => p.slug !== slug).slice(0, 3));
    });
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-32 container-x text-center min-h-screen bg-[#F8F5EF]">
        <Seo title="Beitrag nicht gefunden" path={`/blog/${slug}`} noindex />
        <h1 className="font-display text-4xl text-[#1A1A1A]">Beitrag nicht gefunden</h1>
        <Link to="/blog" className="mt-6 inline-block text-[#FFA500] underline">Zurück zur Übersicht</Link>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="pt-40 pb-32 container-x min-h-screen bg-[#F8F5EF]">
        <p className="text-[#5A5A5A]">Lade Beitrag…</p>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page" className="pt-32 md:pt-40 pb-24 bg-[#F8F5EF] min-h-screen">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${slug}`}
        image={post.cover_image}
        type="article"
      />
      <article className="container-x">
        <Link to="/blog" data-testid="blog-post-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5A5A5A] hover:text-[#FFA500] mb-10 transition-colors">
          <ArrowLeft size={14} /> Zur Übersicht
        </Link>

        <header className="max-w-3xl">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em]">
            <span className="gold-text">{post.category}</span>
            <span className="w-4 h-px bg-[#EAE6DE]"></span>
            <span className="text-[#5A5A5A]">{post.read_time}</span>
          </div>
          <h1 data-testid="blog-post-title" className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-[#1A1A1A] tracking-tight">
            {post.title}
          </h1>
          <p className="mt-6 text-[17px] md:text-[19px] leading-relaxed text-[#4A4A4A] font-light">
            {post.excerpt}
          </p>
        </header>

        <div className="mt-12 img-hover aspect-[16/9] overflow-hidden max-w-5xl">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 lg:col-start-2">
            {renderContent(post.content)}

            <div className="mt-16 p-8 md:p-12 bg-[#1A1A1A] text-[#F8F5EF] relative overflow-hidden">
              <div className="overline text-[#FFA500]">Jetzt anfragen</div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl">
                Möchten Sie Ihre Immobilie professionell präsentieren lassen?
              </h3>
              <p className="mt-4 text-[15px] text-[#C8C5BD] font-light max-w-xl">
                Jetzt Projekt für Immobilienfotografie, Video oder 3D-Tour in NRW anfragen.
              </p>
              <Link
                to="/#kontakt"
                data-testid="blog-post-cta"
                className="group mt-8 inline-flex items-center justify-between px-7 py-4 bg-[#FFA500] text-[#1A1A1A] text-xs uppercase tracking-[0.2em] hover:bg-[#F8F5EF] transition-colors duration-300 min-w-[260px] font-medium"
              >
                Projekt anfragen
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <div className="flex items-center gap-3 mb-4">
              
              <span className="overline">Weitere Beiträge</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-[#1A1A1A] tracking-tight">Das könnte auch interessieren</h3>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group block">
                  <div className="img-hover aspect-[4/3] overflow-hidden mb-4">
                    <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" />
                  </div>
                  <span className="overline text-[10px] gold-text">{r.category}</span>
                  <h4 className="mt-2 font-display text-xl text-[#1A1A1A] group-hover:text-[#FFA500] transition-colors">{r.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </main>
  );
}
